import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { fetchStudentQRCode, fetchTranscript } from '../GoogleSheetsService';
import './Student.css';

const Student = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { studentsData, classMap } = useContext(StudentContext);
  const [qrCode, setQrCode] = useState(null);
  const [studentTranscript, setStudentTranscript] = useState(null);
  const [dayFilters, setDayFilters] = useState({
    'Thứ hai': false,
    'Thứ ba': false,
    'Thứ tư': false,
    'Thứ năm': false,
    'Thứ sáu': false,
    'Thứ bảy': false,
    'Chúa nhật': true
  });

  let studentInfo = null;
  let studentClass = null;
  let classId = null;

  for (const id in studentsData) {
    const student = studentsData[id].find(s => s.id === studentId);
    if (student) {
      studentInfo = student;
      studentClass = classMap[id];
      classId = id;
      break;
    }
  }

  useEffect(() => {
    const loadData = async () => {
      if (studentId) {
        const qrCodeData = await fetchStudentQRCode(studentId);
        setQrCode(qrCodeData);
      }

      if (classId) {
        const transcriptData = await fetchTranscript(classId);
        const transcript = transcriptData[studentId] || null;
        setStudentTranscript(transcript);
      }
    };

    loadData();
  }, [studentId, classId]);

  const handleDayFilterChange = (day) => {
    setDayFilters(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const sortedAttendance = studentInfo
    ? Object.entries(studentInfo.attendance)
      .sort(([dateA], [dateB]) => {
        const [dayA, monthA, yearA] = dateA.split('/').map(Number);
        const [dayB, monthB, yearB] = dateB.split('/').map(Number);
        const dateObjA = new Date(yearA, monthA - 1, dayA);
        const dateObjB = new Date(yearB, monthB - 1, dayB);
        return dateObjA - dateObjB;
      })
      .reverse()
    : [];

  const sortedCatechismAttendance = studentInfo
    ? Object.entries(studentInfo.catechismAttendance)
      .sort(([dateA], [dateB]) => {
        const [dayA, monthA, yearA] = dateA.split('/').map(Number);
        const [dayB, monthB, yearB] = dateB.split('/').map(Number);
        const dateObjA = new Date(yearA, monthA - 1, dayA);
        const dateObjB = new Date(yearB, monthB - 1, dayB);
        return dateObjA - dateObjB;
      })
      .reverse()
    : [];

  const filteredAttendance = sortedAttendance.filter(([date, status]) => {
    const hasSelectedDay = Object.values(dayFilters).some(value => value);
    if (!hasSelectedDay) return true;
    const dayOfWeek = date.split(' - ')[0];
    return dayFilters[dayOfWeek];
  });

  const filteredCatechismAttendance = sortedCatechismAttendance.filter(([date, status]) => {
    const hasSelectedDay = Object.values(dayFilters).some(value => value);
    if (!hasSelectedDay) return true;
    const dayOfWeek = date.split(' - ')[0];
    return dayFilters[dayOfWeek];
  });

  if (!studentInfo) {
    return (
      <div className="student-container">
        <h2>Không tìm thấy thông tin học sinh</h2>
        <button onClick={() => navigate(-1)}>Quay lại</button>
      </div>
    );
  }

  const displayDob = studentInfo.dateOfBirth
    ? new Date(studentInfo.dateOfBirth).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    : 'Chưa có dữ liệu';

  const getStatusClass = (status) => {
    if (status === 'Chưa điểm danh') return 'absent';
    if (status === 'Có xin phép') return 'permitted';
    return 'present';
  };

  return (
    <div className="student-container">
      <div className="student-header">
        <h2>Thông tin chi tiết</h2>
        <button onClick={() => navigate(-1)}>Quay lại</button>
      </div>

      <div className="student-info">
        <div className="info-section">
          <h3>Thông tin cơ bản</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>ID:</label>
              <span>{studentInfo.id}</span>
            </div>
            <div className="info-item">
              <label>Tên Thánh:</label>
              <span>{studentInfo.holyName}</span>
            </div>
            <div className="info-item">
              <label>Họ và Tên:</label>
              <span>{studentInfo.fullName}</span>
            </div>
            <div className="info-item">
              <label>Lớp:</label>
              <span>{studentClass}</span>
            </div>
            <div className="info-item">
              <label>Ngày sinh:</label>
              <span>{displayDob}</span>
            </div>
          </div>
        </div>

        <div className="info-section">
          <h3>QR Code</h3>
          <div className="qr-code-container">
            {qrCode ? (
              <img src={qrCode} alt="QR Code" className="qr-code-image" />
            ) : (
              <p>Không có QR Code</p>
            )}
          </div>
        </div>

        <div className="info-section">
          <h3>Lịch sử điểm danh</h3>
          <div className="day-filters">
            {Object.keys(dayFilters).map(day => (
              <label key={day} className="day-filter-item">
                <input
                  type="checkbox"
                  checked={dayFilters[day]}
                  onChange={() => handleDayFilterChange(day)}
                />
                <span>{day}</span>
              </label>
            ))}
          </div>
          <h4>Đi Lễ</h4>
          <div className="attendance-history">
            {filteredAttendance.map(([date, status]) => (
              <div key={date} className="attendance-item">
                <span className="date">{date}</span>
                <span className={`status ${getStatusClass(status)}`}>
                  {status || 'Chưa điểm danh'}
                </span>
              </div>
            ))}
          </div>
          <h4>Học Giáo Lý</h4>
          <div className="attendance-history">
            {filteredCatechismAttendance.map(([date, status]) => (
              <div key={date} className="attendance-item">
                <span className="date">{date}</span>
                <span className={`status ${getStatusClass(status)}`}>
                  {status || 'Chưa điểm danh'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="info-section">
          <h3>Bảng điểm</h3>
          {studentTranscript ? (
            <div className="transcript-history">
              <div className="transcript-section">
                <h4>Học kỳ I</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Điểm miệng:</label>
                    <span>{studentTranscript.semester1?.oral || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Điểm 15 phút:</label>
                    <span>{studentTranscript.semester1?.fifteenMinutes || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Điểm 1 tiết:</label>
                    <span>{studentTranscript.semester1?.onePeriod || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Điểm thi:</label>
                    <span>{studentTranscript.semester1?.exam || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Trung bình:</label>
                    <span>{studentTranscript.semester1?.average || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Chuyên cần:</label>
                    <span>{studentTranscript.semester1?.diligence || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="transcript-section">
                <h4>Học kỳ II</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Điểm miệng:</label>
                    <span>{studentTranscript.semester2?.oral || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Điểm 15 phút:</label>
                    <span>{studentTranscript.semester2?.fifteenMinutes || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Điểm 1 tiết:</label>
                    <span>{studentTranscript.semester2?.onePeriod || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Điểm thi:</label>
                    <span>{studentTranscript.semester2?.exam || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Trung bình:</label>
                    <span>{studentTranscript.semester2?.average || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Chuyên cần:</label>
                    <span>{studentTranscript.semester2?.diligence || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="transcript-section">
                <h4>Cả năm</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Trung bình cả năm:</label>
                    <span>{studentTranscript.yearly?.average || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Chuyên cần:</label>
                    <span>{studentTranscript.yearly?.diligence || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Xếp hạng:</label>
                    <span>{studentTranscript.yearly?.rank || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Thi vấn đáp</label>
                    <span>{studentTranscript.yearly?.oralExam || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p>Không có dữ liệu bảng điểm</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Student;